import React, { useState, useEffect, useRef } from "react";
import { Search, User, Phone, Building } from "lucide-react";

interface Customer {
  id?: number;
  name: string;
  address?: string;
  postnr?: string;
  sted?: string;
  orgnr?: string;
  tlf?: string;
  email?: string;
}

interface CustomerSearchProps {
  customers?: Customer[];
  onCustomerSelect: (customer: Customer | string) => void;
  onValueChange: (value: string) => void;
  currentValue?: string;
}

const CustomerSearch: React.FC<CustomerSearchProps> = ({ 
  customers = [], 
  onCustomerSelect, 
  onValueChange,
  currentValue = "" 
}) => {
  const [suggestions, setSuggestions] = useState<Customer[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Lukk dropdown når man klikker utenfor
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchCustomers = (term: string) => {
    if (!term || term.length < 1) {
      setSuggestions([]);
      return;
    }

    const searchTermLower = term.toLowerCase().trim();
    
    const filtered = customers.filter(customer => {
      const name = customer.name ? customer.name.toLowerCase() : '';
      const phone = customer.tlf || '';
      const orgnr = customer.orgnr || '';
      
      return (
        name.includes(searchTermLower) ||
        phone.includes(searchTermLower) ||
        orgnr.includes(searchTermLower)
      );
    }).sort((a, b) => {
      const aName = a.name ? a.name.toLowerCase() : '';
      const bName = b.name ? b.name.toLowerCase() : '';
      
      // Eksakt match først
      if (aName === searchTermLower && bName !== searchTermLower) return -1;
      if (bName === searchTermLower && aName !== searchTermLower) return 1;
      
      // Deretter "starter med"
      if (aName.startsWith(searchTermLower) && !bName.startsWith(searchTermLower)) return -1;
      if (!aName.startsWith(searchTermLower) && bName.startsWith(searchTermLower)) return 1;
      
      return aName.localeCompare(bName, 'nb-NO');
    }).slice(0, 20);

    setSuggestions(filtered);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Søk etter kunder
    searchCustomers(value);
    setShowSuggestions(true);
    setSelectedIndex(-1);
    
    // Send verdien oppover - DETTE ER DET VIKTIGSTE!
    onValueChange(value);
  };

  const handleCustomerSelect = (customer: Customer) => {
    setShowSuggestions(false);
    setSelectedIndex(-1);
    setSuggestions([]);
    
    // Send hele customer objektet oppover
    onCustomerSelect(customer);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < suggestions.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleCustomerSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={currentValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder="Søk etter kunde..."
          className="w-full px-3 py-2 pl-9 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((customer, index) => (
            <div
              key={customer.id || index}
              onClick={() => handleCustomerSelect(customer)}
              onMouseEnter={() => setSelectedIndex(index)}
              className={`px-3 py-2 cursor-pointer transition-colors ${
                index === selectedIndex ? 'bg-green-50' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-3">
                <User className="text-gray-400" size={16} />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{customer.name}</div>
                  <div className="text-sm text-gray-500 space-x-4">
                    {customer.tlf && (
                      <span className="inline-flex items-center space-x-1">
                        <Phone size={12} />
                        <span>{customer.tlf}</span>
                      </span>
                    )}
                    {customer.orgnr && (
                      <span className="inline-flex items-center space-x-1">
                        <Building size={12} />
                        <span>{customer.orgnr}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerSearch;